import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import {
  QrCode, Loader2, AlertCircle, ChevronLeft, Search,
  CheckCircle2, Coffee, Package, CalendarCheck, X, User, ArrowLeft,
} from "lucide-react";
import {
  useLazyAdminSearchRegistrantQuery,
  useLazyScannerLookupQuery,
  useScannerMarkAttendanceMutation,
  useScannerMarkRefreshmentMutation,
  useScannerUpdateMaterialsMutation,
} from "../api/adminEventsApi";

function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? err?.message ?? "An error occurred";
}
function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

function AdminScannerPageInner() {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ── Search state ─────────────────────────────────────────────────────────
  const [query, setQuery]               = useState("");
  const [searchErr, setSearchErr]       = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedReg, setSelectedReg]   = useState<any | null>(null);
  const [lookupData, setLookupData]     = useState<any | null>(null);
  const [lookupErr, setLookupErr]       = useState("");

  // ── Action state ─────────────────────────────────────────────────────────
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busyDayAtt, setBusyDayAtt] = useState<number | null>(null);
  const [busyDayRef, setBusyDayRef] = useState<number | null>(null);
  const [busyMat, setBusyMat]       = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // ── RTK hooks ────────────────────────────────────────────────────────────
  const [triggerSearch, { isFetching: searching }] = useLazyAdminSearchRegistrantQuery();
  const [triggerLookup, { isFetching: loading }]   = useLazyScannerLookupQuery();
  const [markAttendance]  = useScannerMarkAttendanceMutation();
  const [markRefreshment] = useScannerMarkRefreshmentMutation();
  const [updateMaterials] = useScannerUpdateMaterialsMutation();

  // ── Helpers ───────────────────────────────────────────────────────────────
  const notify = (ok: boolean, text: string) => {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  };

  // No `preferCacheValue` on the trigger: this is called again after every
  // check-in, food issue and material issue, and returning the cached payload
  // from before the action left the screen showing the old state until the page
  // was reloaded by hand.
  const loadDetail = async (barcode: string) => {
    setLookupErr("");
    setLookupData(null);
    try {
      const result = await triggerLookup(barcode).unwrap();
      setLookupData(result);
    } catch (err: any) {
      setLookupErr(extractError(err));
    }
  };

  const reloadDetail = () => {
    const barcode = selectedReg?.barcode_data ?? lookupData?.barcode_data;
    if (barcode) loadDetail(barcode);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    const q = query.trim();
    if (!q || !eventId) return;
    setSearchErr("");
    setSearchResults([]);
    setSelectedReg(null);
    setLookupData(null);
    setLookupErr("");
    setActionMsg(null);

    try {
      // Also uncached: a delegate registered or paid up a minute ago must show
      // in a search the desk runs now.
      const results = await triggerSearch({ eventId, q }).unwrap();
      if (results.length === 0) {
        setSearchErr("No registrant found matching that search.");
      } else if (results.length === 1) {
        // auto-select single match
        handleSelect(results[0]);
      } else {
        setSearchResults(results);
      }
    } catch (err: any) {
      setSearchErr(extractError(err));
    }
  };

  // ── Select registrant ─────────────────────────────────────────────────────
  const handleSelect = (reg: any) => {
    setSelectedReg(reg);
    setSearchResults([]);
    setActionMsg(null);
    if (reg.barcode_data) {
      loadDetail(reg.barcode_data);
    } else {
      setLookupErr("This registrant has no QR code — attendance/food actions unavailable.");
    }
  };

  // ── Back to search ────────────────────────────────────────────────────────
  const handleClear = () => {
    setSelectedReg(null);
    setLookupData(null);
    setLookupErr("");
    setSearchResults([]);
    setActionMsg(null);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Attendance ────────────────────────────────────────────────────────────
  const handleMarkAttendance = async (dayId: number) => {
    const barcode = selectedReg?.barcode_data;
    if (!barcode) return;
    setBusyDayAtt(dayId);
    try {
      await markAttendance({ barcode, event_day_id: dayId }).unwrap();
      notify(true, "Attendance marked.");
      reloadDetail();
    } catch (err) { notify(false, extractError(err)); }
    setBusyDayAtt(null);
  };

  // ── Refreshment ───────────────────────────────────────────────────────────
  const handleMarkRefreshment = async (dayId: number) => {
    const barcode = selectedReg?.barcode_data;
    if (!barcode) return;
    setBusyDayRef(dayId);
    try {
      await markRefreshment({ barcode, event_day_id: dayId }).unwrap();
      notify(true, "Refreshment issued.");
      reloadDetail();
    } catch (err) { notify(false, extractError(err)); }
    setBusyDayRef(null);
  };

  // ── Materials ─────────────────────────────────────────────────────────────
  const handleIssueMaterial = async (eventMaterialId: number) => {
    const regId = selectedReg?.id ?? lookupData?.id ?? lookupData?.registration_id;
    if (!regId) return;
    setBusyMat(eventMaterialId);
    try {
      await updateMaterials({
        registrationId: regId,
        material_updates: [{ event_material_id: eventMaterialId, is_collected: true }],
      }).unwrap();
      notify(true, "Material issued.");
      reloadDetail();
    } catch (err) { notify(false, extractError(err)); }
    setBusyMat(null);
  };

  const days: any[]         = lookupData?.event?.days ?? [];
  const attendance: any[]   = lookupData?.attendance ?? [];
  const materials: any[]    = lookupData?.materials ?? [];
  const refreshments: any[] = lookupData?.refreshment_choices ?? [];

  const noBarcode = selectedReg && !selectedReg.barcode_data;

  // Unsettled fee — the API refuses every on-site action, so disable them here
  // and point the operator at the registrations tab, where they can record it.
  const paymentBlocked =
    lookupData?.payment_blocked ??
    (selectedReg ? selectedReg.payment_status === "pending" : false);
  const blocked = !!noBarcode || !!paymentBlocked;

  return (
    <div className="space-y-6 max-w-3xl mx-auto p-4 md:p-6 pb-20">

      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors">
        <ChevronLeft className="w-4 h-4" /> Back to Event
      </button>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
            <QrCode className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">On-Site Scanner</h1>
            <p className="text-gray-400 text-sm mt-1.5 font-medium">
              Search by name, email, reg. no., or membership no. — or scan a QR code directly.
            </p>
          </div>
        </div>
      </div>

      {/* Search box */}
      {!selectedReg && (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Find Registrant</div>
          <div className="flex gap-3">
            <input
              ref={inputRef}
              autoFocus
              className="flex-1 border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm"
              placeholder="Name, email, reg. no., membership no., or QR scan…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            />
            <button
              onClick={handleSearch}
              disabled={searching || !query.trim()}
              className="flex items-center gap-2 bg-gray-900 text-white font-black text-sm px-5 py-2.5 rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </button>
          </div>

          {searchErr && (
            <div className="mt-3 flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
              <AlertCircle className="w-4 h-4 shrink-0" /> {searchErr}
            </div>
          )}

          {/* Results list */}
          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} — select to proceed
              </div>
              {searchResults.map((r: any) => (
                <button
                  key={r.id}
                  onClick={() => handleSelect(r)}
                  className="w-full flex items-center gap-4 p-4 bg-gray-50 hover:bg-indigo-50 hover:border-indigo-100 border border-transparent rounded-2xl transition-colors text-left group"
                >
                  <div className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center shrink-0 group-hover:bg-indigo-100 transition-colors">
                    <User className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-gray-800 text-sm truncate">{r.attendee_name ?? r.user?.name}</div>
                    <div className="text-xs text-gray-400 font-medium truncate">{r.attendee_email ?? r.user?.email}</div>
                    <div className="text-[10px] font-black text-indigo-600 mt-0.5">{r.registration_no}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${
                      r.payment_status === "paid" || r.payment_status === "free"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                        : "bg-amber-50 text-amber-700 border-amber-100"
                    }`}>{r.payment_status}</span>
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${
                      r.overall_attendance === "present" ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                      r.overall_attendance === "partial"  ? "bg-amber-50 text-amber-700 border-amber-100" :
                      "bg-gray-100 text-gray-400 border-gray-100"
                    }`}>{r.overall_attendance ?? "absent"}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected registrant header */}
      {selectedReg && (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
              <CalendarCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-gray-900 text-base">{selectedReg.attendee_name ?? selectedReg.user?.name}</div>
              <div className="text-sm text-gray-400 font-medium">{selectedReg.attendee_email ?? selectedReg.user?.email}</div>
              <div className="text-xs font-black text-indigo-600 mt-1">{selectedReg.registration_no}</div>
              {selectedReg.user?.member?.membership_no && (
                <div className="text-xs font-bold text-gray-400 mt-0.5">
                  Member: {selectedReg.user.member.membership_no}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${
                  (lookupData?.overall_attendance ?? selectedReg.overall_attendance) === "present"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : (lookupData?.overall_attendance ?? selectedReg.overall_attendance) === "partial"
                    ? "bg-amber-50 text-amber-700 border-amber-100"
                    : "bg-gray-100 text-gray-400 border-gray-100"
                }`}>
                  Attendance: {lookupData?.overall_attendance ?? selectedReg.overall_attendance ?? "absent"}
                </span>
                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${
                  selectedReg.payment_status === "paid" || selectedReg.payment_status === "free"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : "bg-amber-50 text-amber-700 border-amber-100"
                }`}>{selectedReg.payment_status}</span>
                {selectedReg.refreshment_mode && (
                  <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border bg-gray-50 text-gray-500 border-gray-100">
                    {selectedReg.refreshment_mode}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={handleClear}
              className="flex items-center gap-1.5 text-xs font-black text-gray-400 hover:text-gray-700 transition-colors shrink-0 mt-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> New search
            </button>
          </div>

          {noBarcode && (
            <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-100 text-amber-700 text-xs font-medium px-4 py-3 rounded-2xl">
              <AlertCircle className="w-4 h-4 shrink-0" />
              No QR code for this registration — attendance and food actions are unavailable.
            </div>
          )}
          {paymentBlocked && (
            <div className="mt-4 flex items-start gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-xs font-medium px-4 py-3 rounded-2xl">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <strong className="font-black">Payment outstanding</strong>
                {lookupData?.amount_due != null ? ` — ₦${Number(lookupData.amount_due).toLocaleString()}` : ""}.
                Check-in, food and materials are locked until it is settled.{" "}
                <button
                  onClick={() => navigate(`/admin/events/${eventId}`)}
                  className="underline font-black"
                >
                  Record payment
                </button>
                .
              </span>
            </div>
          )}
          {lookupErr && (
            <div className="mt-4 flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-xs font-medium px-4 py-3 rounded-2xl">
              <AlertCircle className="w-4 h-4 shrink-0" /> {lookupErr}
            </div>
          )}
          {loading && (
            <div className="mt-4 flex items-center gap-2 text-xs font-medium text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading registration detail…
            </div>
          )}
        </div>
      )}

      {/* Action feedback */}
      {actionMsg && (
        <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border text-sm font-bold ${actionMsg.ok ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"}`}>
          {actionMsg.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {actionMsg.text}
          <button onClick={() => setActionMsg(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── Attendance per day ──────────────────────────────────────────────── */}
      {lookupData && days.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-emerald-500" />
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Attendance</div>
            </div>
            <div className="text-xs font-black text-gray-500">
              {attendance.filter((a: any) => a.is_present).length}/{days.length} present
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {days.map((day: any) => {
              const rec = attendance.find((a: any) => a.event_day_id === day.id || a.day_id === day.id);
              const isPresent = !!rec?.is_present;
              const busy = busyDayAtt === day.id;
              return (
                <div key={day.id} className={`flex items-center justify-between px-6 py-4 transition-colors ${isPresent ? "bg-emerald-50" : "bg-white hover:bg-gray-50"}`}>
                  <div>
                    <div className={`font-bold text-sm ${isPresent ? "text-emerald-800" : "text-gray-800"}`}>{day.day_label}</div>
                    <div className="text-xs text-gray-400 font-medium">{day.day_date ? fmt(day.day_date) : ""}</div>
                  </div>
                  {isPresent ? (
                    <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1.5 shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Present
                    </span>
                  ) : (
                    <button
                      onClick={() => handleMarkAttendance(day.id)}
                      disabled={busy || blocked}
                      className="text-xs font-black px-4 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50 transition-colors flex items-center gap-1.5 shrink-0"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarCheck className="w-3.5 h-3.5" />}
                      Mark Present
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Refreshments ────────────────────────────────────────────────────── */}
      {/* refreshment_choices is per-day: { event_day_id, day_label, chosen_name, available_options, is_collected } */}
      {lookupData && refreshments.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <Coffee className="w-4 h-4 text-rose-500" />
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Refreshments / Food</div>
            </div>
            <div className="text-xs font-black text-gray-500">
              {refreshments.filter((c: any) => c.is_collected).length}/{refreshments.length} issued
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {refreshments.map((dayRef: any) => {
              const dayId = dayRef.event_day_id;
              const busy = busyDayRef === dayId;
              return (
                <div key={dayId} className={`flex items-center justify-between px-6 py-4 transition-colors ${dayRef.is_collected ? "bg-emerald-50" : "bg-white hover:bg-rose-50/40"}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <div className={`font-bold text-sm ${dayRef.is_collected ? "text-emerald-800" : "text-gray-800"}`}>{dayRef.day_label}</div>
                    {dayRef.chosen_name ? (
                      <div className="text-xs font-bold text-indigo-600 mt-0.5">Choice: {dayRef.chosen_name}</div>
                    ) : (
                      <div className="text-xs text-gray-400 font-medium mt-0.5">
                        {(dayRef.available_options ?? []).map((o: any) => o.name).join(" · ")}
                      </div>
                    )}
                    {dayRef.is_collected && dayRef.collected_at && (
                      <div className="text-[10px] text-emerald-600 font-bold mt-0.5">Issued {fmt(dayRef.collected_at)}</div>
                    )}
                  </div>
                  {dayRef.is_collected ? (
                    <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1.5 shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Issued
                    </span>
                  ) : (
                    <button
                      onClick={() => handleMarkRefreshment(dayId)}
                      disabled={busy || blocked}
                      className="text-xs font-black px-4 py-2 rounded-xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 transition-colors flex items-center gap-1.5 shrink-0"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coffee className="w-3.5 h-3.5" />}
                      Issue Food
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Materials ────────────────────────────────────────────────────────── */}
      {lookupData && materials.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-500" />
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Materials</div>
            </div>
            <div className="text-xs font-black text-gray-500">
              {materials.filter((m: any) => m.is_collected).length}/{materials.length} issued
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {materials.map((m: any) => {
              const busy = busyMat === m.id;
              return (
                <div key={m.id} className={`flex items-center justify-between px-6 py-4 transition-colors ${m.is_collected ? "bg-emerald-50" : "bg-white hover:bg-gray-50"}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <div className={`font-bold text-sm flex items-center gap-2 ${m.is_collected ? "text-emerald-800" : "text-gray-800"}`}>
                      {m.is_collected && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                      {m.name}
                    </div>
                    {m.description && <div className="text-xs text-gray-400 font-medium">{m.description}</div>}
                    {m.quantity_total != null && (
                      <div className="text-[10px] font-bold mt-0.5">
                        <span className={m.quantity_remaining === 0 ? "text-rose-600" : "text-gray-400"}>
                          {m.quantity_remaining ?? 0} remaining of {m.quantity_total}
                          {m.quantity_remaining === 0 ? " — DEPLETED" : ""}
                        </span>
                      </div>
                    )}
                    {m.is_collected && m.collected_at && (
                      <div className="text-[10px] text-emerald-600 font-bold mt-0.5">Issued {fmt(m.collected_at)}</div>
                    )}
                  </div>
                  {m.is_collected ? (
                    <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                      Issued ✓
                    </span>
                  ) : (
                    <button
                      onClick={() => handleIssueMaterial(m.id)}
                      disabled={busy || blocked}
                      className="text-xs font-black px-3 py-2 rounded-xl bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors flex items-center gap-1.5 shrink-0"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
                      Issue
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <QrCode className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">Scanner Tips</div>
            <p className="font-bold text-sm text-white/80">
              Search by any registrant detail, or point a barcode scanner at a QR code — it will type the code and look up instantly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The door scanner is a standalone job — desk staff hold this and nothing else.
 */
export default function AdminScannerPage() {
  return (
    <PageGate perm={PERMS.event.scannerUse}>
      <AdminScannerPageInner />
    </PageGate>
  );
}
